(() => {
  if (customElements.get('sitone-about-video')) return;

  class SitoneAboutVideo extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.preview = this.querySelector('[data-sitone-ambient]');
      this.dialog = this.querySelector('dialog');
      this.player = this.dialog?.querySelector('video');
      this.opener = this.querySelector('[data-sitone-video-open]');
      if (!this.preview || !this.dialog || !this.player || !this.opener) return;

      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.opener.addEventListener('click', () => this.open(), { signal });
      this.querySelector('[data-sitone-video-close]').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('close', () => this.finish(true), { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, { signal });
      this.preview.addEventListener('loadedmetadata', () => this.updateRatio(), { signal });
      this.player.addEventListener('loadedmetadata', () => { this.updateRatio(); this.seekToPreview(); }, { signal });
      this.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => this.dispose(), { signal });
      this.updateRatio();
    }

    updateRatio() {
      const source = [this.preview, this.player].find(video => video.videoWidth > 0 && video.videoHeight > 0);
      if (source) this.style.setProperty('--sitone-video-ratio', String(source.videoWidth / source.videoHeight));
    }

    open() {
      if (this.dialog.open || typeof this.dialog.showModal !== 'function') return;
      this.beforeOpen = { playing: !this.preview.paused, userPaused: this.preview.dataset.userPaused };
      // Keep the shared ambient observer from restarting the background video.
      this.preview.dataset.userPaused = 'true';
      this.preview.pause();
      try {
        this.dialog.showModal();
      } catch (_) {
        this.finish(true);
        return;
      }
      document.documentElement.classList.add('sitone-about-video-open');
      this.pendingTime = this.preview.currentTime;
      this.player.muted = true;
      this.seekToPreview();
      this.player.play().then(() => {
        if (!this.dialog.open || !this.isConnected) this.player.pause();
      }).catch(() => { /* Native controls remain available if playback is blocked. */ });
    }

    seekToPreview() {
      if (!this.dialog.open || this.pendingTime == null || this.player.readyState < 1) return;
      try {
        this.player.currentTime = this.pendingTime;
        this.pendingTime = null;
      } catch (_) { /* Retain the pending time until metadata becomes available. */ }
    }

    finish(restorePlayback) {
      this.player.pause();
      this.pendingTime = null;
      const previous = this.beforeOpen;
      this.beforeOpen = null;
      if (!document.querySelector('sitone-about-video dialog[open]')) document.documentElement.classList.remove('sitone-about-video-open');
      if (!previous) return;
      if (previous.userPaused === undefined) delete this.preview.dataset.userPaused;
      else this.preview.dataset.userPaused = previous.userPaused;
      if (!restorePlayback || !this.isConnected) return;

      this.opener.focus({ preventScroll: true });
      const rect = this.preview.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      const visible = rect.width * rect.height > 0 && visibleWidth * visibleHeight / (rect.width * rect.height) >= 0.25;
      if (previous.playing && visible && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.preview.play().then(() => {
          if (this.dialog.open || !this.isConnected) this.preview.pause();
        }).catch(() => {});
      }
    }

    dispose() {
      this.controller?.abort();
      this.controller = null;
      this.preview?.pause();
      if (this.dialog?.open) this.dialog.close();
      if (this.player) this.finish(false);
    }

    disconnectedCallback() {
      this.dispose();
    }
  }

  customElements.define('sitone-about-video', SitoneAboutVideo);
})();
